import React, { useRef, useState, useEffect } from 'react';
import { Container, Grid, GridItem, Flex, Link, Text, Box } from '@chakra-ui/react';
import { PortableText, toPlainText } from '@portabletext/react';
import { useStaticQuery, graphql, withPrefix } from 'gatsby';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Hero } from '../components/Hero';
import { components } from '../components/RichText/components';
import { ParallaxCard } from '../components/ParallaxCard';
import { ContinueReading } from '../components/ContinueReading';

const FadedPortableText = ({ value, components }) => (
  <Box position='relative' maxHeight='50rem' overflow='hidden' className='chakra-text'>
    <PortableText value={value} components={components} />
    <Box
      position='absolute'
      bottom='0'
      left='0'
      right='0'
      height='12.5rem'
      background='linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))'
    />
  </Box>
);

const ReadMore = ({ data, rightToLeft }) => {
  const { sectionTitle, pageTitle, pageDescription, link } = data || {};

  return (
    <Box as='section' background='zinc.100'>
      <Container maxWidth='container.lg' paddingY={{ base: 30, md: 60 }}>
        <Text as='h2' textStyle='h3' textAlign={rightToLeft ? 'right' : 'left'}>
          {sectionTitle}
        </Text>
        <Link
          href={link}
          variant='ghost'
          mt={20}
          display='block'
          _hover={{
            textDecoration: 'none',
            color: 'navy',
          }}
        >
          <Flex direction={{ base: 'column', md: 'row' }} gap={30} alignItems='center'>
            <Box flexBasis={{ base: '100%', md: '50%' }} flexGrow={0} flexShrink={0}>
              <img
                src={`${withPrefix('/')}readmore.jpg`}
                alt='A blue flag with the IMF logo'
                style={{
                  width: '100%',
                  height: 'auto',
                  objectFit: 'cover',
                }}
              />
            </Box>
            <Box
              flexBasis={{ base: '100%', md: '50%' }}
              flexGrow={0}
              flexShrink={0}
              textAlign={rightToLeft ? 'right' : 'left'}
            >
              <Text fontSize='lg' fontWeight='bold' mt={10}>
                {pageTitle}
              </Text>

              <Text fontSize={16}>{pageDescription}</Text>
            </Box>
          </Flex>
        </Link>
      </Container>
    </Box>
  );
};

export default function Download({ pageContext }) {
  const { body, download, rightToLeft, title, mdQuote, readMore, continueReading, richTitle } =
    pageContext || {};

  const rightColumnRef = useRef(null);
  const [isRightSticky, setIsRightSticky] = useState(false);
  const heroes = useStaticQuery(HeroQuery);
  const { mdHero, introHero } = heroes || {};
  const { parallaxHero } = introHero || {};

  if (richTitle) {
    parallaxHero.richTitle = richTitle;
  }

  const DownloadButton = ({ isPrimary }) =>
    download?.pdfDownloadLink ? (
      <Link
        variant={isPrimary ? 'buttonPrimary' : 'buttonSecondary'}
        href={`${download.pdfDownloadLink.asset.url}/${download.pdfDownloadLink.asset.originalFilename}`}
        download
        textAlign='center'
        display='inline-block'
        alignSelf='center'
        fontWeight={600}
      >
        {download.cta}
      </Link>
    ) : null;

  useEffect(() => {
    const checkSticky = () => {
      const windowHeight = window.innerHeight - 100;
      const windowWidth = window.innerWidth;
      const rightColumnHeight = rightColumnRef?.current?.clientHeight;

      if (windowWidth >= 1280) {
        setIsRightSticky(rightColumnHeight < windowHeight);
      } else {
        setIsRightSticky(false);
      }
    };

    checkSticky();

    window.addEventListener('resize', checkSticky);
    return () => {
      window.removeEventListener('resize', checkSticky);
    };
  }, []);

  return (
    <Layout>
      <Hero data={{ title, parallaxHero }} alignItems='center' textAlign='center'>
        <DownloadButton isPrimary />
      </Hero>
      <Box position='relative' zIndex='1' width='100%' background='white'>
        <Container
          display='flex'
          flexDirection='column'
          maxWidth='container.xl'
          gap={18}
          pb={{ base: 30, md: 60 }}
          px={{ base: 0 }}
          sx={{
            direction: rightToLeft ? 'rtl' : 'ltr',
          }}
        >
          <Grid
            gridTemplateColumns={{
              base: '1fr',
              xl: 'minmax(0, 3fr) minmax(0, 7fr) minmax(0, 3fr)',
            }}
            gap={30}
            alignItems='center'
            mx='auto'
          >
            <GridItem />

            <GridItem px={{ base: 30, xl: 0 }} className='section'>
              <FadedPortableText value={body} components={components} />
            </GridItem>

            <GridItem
              display='flex'
              flexDirection='column'
              gap={30}
              position={isRightSticky ? 'sticky' : 'static'}
              top='7.1875rem'
              px={30}
              className='rightColumn'
              ref={rightColumnRef}
            >
              {mdQuote && (
                <Flex direction='column' gap={10} textAlign={rightToLeft ? 'right' : 'left'}>
                  <ParallaxCard images={mdHero.data.images} aspectRatio={mdHero.data.aspectRatio} />
                  <Text fontSize={16} fontWeight={400} mt={5}>
                    {mdQuote.quote}
                  </Text>
                  <Flex direction='column'>
                    <Text textTransform='uppercase' fontWeight={700} fontSize={16}>
                      {mdQuote?.name || 'Kristalina Georgieva'}
                    </Text>
                    <Text fontSize={16}>{mdQuote.position}</Text>
                  </Flex>
                  <Box display={{ base: 'none', xl: 'block' }}>
                    <DownloadButton />
                  </Box>
                </Flex>
              )}
            </GridItem>
          </Grid>
        </Container>
      </Box>

      {continueReading && (
        <ContinueReading data={continueReading} rightToLeft={rightToLeft}>
          <DownloadButton />
        </ContinueReading>
      )}

      {readMore && <ReadMore data={readMore} rightToLeft={rightToLeft} />}
    </Layout>
  );
}

const languageAttribute = (slug) => {
  switch (slug) {
    case 'arabic':
      return 'ar';
    case 'chinese':
      return 'cn';
    case 'french':
      return 'fr';
    case 'russian':
      return 'ru';
    case 'spanish':
      return 'es';
    case 'portuguese':
      return 'pt';
    case 'dutch':
      return 'nl';
    case 'german':
      return 'de';
    case 'japanese':
      return 'ja';
    default:
      return 'en';
  }
};

export const Head = ({ pageContext }) => {
  const { slug, richTitle } = pageContext || {};
  const seoTitle = toPlainText(richTitle);

  return <SEO title={seoTitle} language={languageAttribute(slug?.current)} />;
};

const HeroQuery = graphql`
  query HeroQuery {
    mdHero: sanityPage(title: { eq: "Message from the Managing Director" }) {
      data: _rawParallaxHero
    }
    introHero: sanityPage(title: { eq: "Resilience in the Face of Change" }) {
      parallaxHero {
        images {
          ...ImageWithPreview
          asset {
            description
          }
        }
        aspectRatio
      }
    }
  }
`;
